// Scroll-settle detection for the rail's page-navigation jumps.
//
// jumpToPage suppresses the panes' scroll handlers (spy + sync) while its
// smooth scrolls animate. A fixed release timer can expire mid-animation —
// the 700ms guard died before a full-document jump's ~900ms scroll finished,
// and the animation tail re-entered the sync handler and dragged the pane
// with FEWER pages to its bottom (2026-08-05 Simplicity run, defect 2, only
// visible when the sides' page counts differ). Release must therefore follow
// the ACTUAL animation: watch the positions until they stop moving.
// (`scrollend` would be the native signal, but Safari doesn't ship it.)

// Consecutive identical-position frames that count as "settled". Smooth
// scrolling moves every frame (fractional scrollTop), so a short run of
// frozen frames only happens once the animation is done — or never started
// (jump to the current page scrolls nothing and releases in ~3 frames).
export const SETTLE_FRAMES = 3;

// Hard backstop: never leave the guard stuck (rAF pauses in a hidden tab;
// a held wheel mid-jump keeps positions moving indefinitely).
export const SETTLE_MAX_MS = 2500;

/**
 * Watch scroll positions each animation frame; fire onSettled once they have
 * been identical for `frames` consecutive frames, or unconditionally after
 * `maxMs`. Returns a cancel function that stops watching WITHOUT settling.
 *
 * @param readPositions () => number[] — current scrollTop of each pane
 * @param onSettled     () => void — called exactly once
 */
export function watchScrollSettle(readPositions, onSettled, { frames = SETTLE_FRAMES, maxMs = SETTLE_MAX_MS } = {}) {
	let rafId = null;
	let done = false;
	let last = null;
	let stableFrames = 0;

	const settle = () => {
		if (done)
			return;
		done = true;
		cancelAnimationFrame(rafId);
		clearTimeout(backstopId);
		onSettled();
	};

	const backstopId = setTimeout(settle, maxMs);

	const tick = () => {
		if (done)
			return;
		const positions = readPositions();
		const moved = last === null
			|| positions.length !== last.length
			|| positions.some((p, i) => p !== last[i]);
		if (moved) {
			stableFrames = 0;
			last = positions;
		} else {
			stableFrames++;
		}
		if (stableFrames >= frames) {
			settle();
			return;
		}
		rafId = requestAnimationFrame(tick);
	};
	rafId = requestAnimationFrame(tick);

	return () => {
		done = true;
		cancelAnimationFrame(rafId);
		clearTimeout(backstopId);
	};
}
